import { memo, useState } from "react";
import { Box, Button, IconButton, Typography, TextField, styled } from "@mui/material";
import { Pencil, X } from "lucide-react";

// Components
import DateTextBox from "../common/DateTextBox";
import DimmerLoader from "./common/DimmerLoader";

// Interfaces
import { getStaticUrlNews } from "./MeGenInterface";

// Utils
import moment from "moment";
import { beStringToAdString } from "react-lib/utils/dateUtils";

// Types
import { UpdateMetadataPayload } from "./types/MeGenTypes";

type CardGenerateVideoThumbnailProps = {
  thumbnailPath: string;
  thumbnailPrompt: string;
  youtubeTitle: string;
  youtubeDescription: string;
  thumbnailDate?: string;
  onSaveMetadata: (data: UpdateMetadataPayload) => void;
  mediaVersion?: string;
  isRunning?: boolean;
};

// Styles
const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  border: "1px solid #e2e8f0",
  padding: "1.5rem",
  marginBottom: "1.5rem",
});

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "1.5rem",
});

// Main Component
const CardGenerateVideoThumbnail = (props: CardGenerateVideoThumbnailProps) => {

  const currentDate = moment().format("DD/MM/YYYY");

  const [editingMetadata, setEditingMetadata] = useState(false);
  const [metadataForm, setMetadataForm] = useState<UpdateMetadataPayload>({
    youtube_title: "",
    thumbnail_prompt: "",
    thumbnail_date: currentDate,
  });

  const handleSave = () => {
    props.onSaveMetadata(metadataForm);
    setEditingMetadata(false);
  };

  const rawThumbnailUrl = getStaticUrlNews(props.thumbnailPath) || "";
  const thumbnailUrl = rawThumbnailUrl && props.mediaVersion ? `${rawThumbnailUrl}?v=${props.mediaVersion}` : rawThumbnailUrl;

  return (
    <Card id="megen-thumbnail-root" sx={{ position: "relative", overflow: "hidden" }}>
      <DimmerLoader
        active={!!props.isRunning}
        text={"Generating Thumbnail..."}
        inverted
        size="medium"
        // styles={{ borderRadius: "0.75rem" }}
      />
      <Header id="megen-thumbnail-header">
        <Typography id="megen-thumbnail-title" sx={{ fontSize: "1.125rem", fontWeight: 600, color: "#1e293b" }}>
          Thumbnail & Metadata
        </Typography>
        {!editingMetadata ? (
          <IconButton
            id="megen-thumbnail-edit-btn"
            onClick={() => {
              setEditingMetadata(true);
              setMetadataForm({
                youtube_title: props.youtubeTitle || "",
                thumbnail_prompt: props.thumbnailPrompt || "",
                thumbnail_date: props.thumbnailDate || currentDate,
              });
            }}
            title="Edit Metadata"
          >
            <Pencil size={18} />
          </IconButton>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              id="megen-thumbnail-save-btn"
              variant="contained"
              size="small"
              onClick={handleSave}
              sx={{ bgcolor: "#16a34a" }}
            >
              Save & Regenerate
            </Button>
            <IconButton id="megen-thumbnail-cancel-btn" onClick={() => setEditingMetadata(false)}>
              <X size={18} />
            </IconButton>
          </Box>
        )}
      </Header>
      <Box id="megen-thumbnail-content" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box id="megen-thumbnail-yt-title-section">
          <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>YouTube Title</Typography>
          {editingMetadata ? (
            <TextField
              fullWidth
              size="small"
              value={metadataForm.youtube_title}
              onChange={(e) => setMetadataForm({ ...metadataForm, youtube_title: e.target.value })}
              sx={{ mt: 0.5 }}
            />
          ) : (
            <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "#1e293b", mt: 0.5 }}>
              {props.youtubeTitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
              Thumbnail Preview
            </Typography>
            <img
              id="megen-thumbnail-preview"
              src={thumbnailUrl}
              alt="Thumbnail"
              style={{
                width: "100%",
                borderRadius: "0.5rem",
                border: "1px solid #e2e8f0",
                marginTop: "0.5rem",
              }}
            />
          </Box>

          {/* --- Thumbnail Prompt Section --- */}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
              Thumbnail Prompt (AI)
            </Typography>
            {editingMetadata ? (
              <TextField
                fullWidth
                multiline
                minRows={4}
                size="small"
                value={metadataForm.thumbnail_prompt}
                onChange={(e) =>
                  setMetadataForm({ ...metadataForm, thumbnail_prompt: e.target.value })
                }
                sx={{
                  mt: 1,
                  "& .MuiInputBase-inputMultiline": {
                    resize: "vertical",
                    overflow: "auto",
                    maxWidth: "100%",
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: "#f8fafc",
                  borderRadius: "0.5rem",
                  border: "1px solid #e2e8f0",
                  fontSize: "0.875rem",
                  color: "#64748b",
                  fontStyle: "italic",
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {props.thumbnailPrompt || "(No prompt recorded)"}
              </Box>
            )}

            {/* --- Date Picker Section --- */}
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b", marginTop: "1rem" }}>
              Thumbnail Date
            </Typography>
            {editingMetadata ? (
              <DateTextBox
                value={metadataForm.thumbnail_date || ""}
                onChange={(date: string) => {
                  const formattedDate = beStringToAdString(date);
                  setMetadataForm({ ...metadataForm, thumbnail_date: formattedDate });
                }}
                placeholder="DD/MM/YYYY"
                useAdDate={true}
                adFormat="DD/MM/YYYY"
                style={{ marginTop: "0.25rem", width: "100%" }}
                inputStyle={{ width: "100%" }}
              />
            ) : (
              <DateTextBox
                disabled={true}
                value={metadataForm.thumbnail_date || ""}
                placeholder="DD/MM/YYYY"
                useAdDate={true}
                adFormat="DD/MM/YYYY"
                style={{ marginTop: "0.25rem", width: "100%" }}
                inputStyle={{ width: "100%" }}
              />
            )}
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
            YouTube Description
          </Typography>
          <Box
            sx={{
              mt: 0.5,
              p: 1.5,
              bgcolor: "#f8fafc",
              borderRadius: "0.5rem",
              border: "1px solid #e2e8f0",
              fontSize: "0.875rem",
              color: "#1e293b",
              whiteSpace: "pre-wrap",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {props.youtubeDescription || "(No description)"}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

CardGenerateVideoThumbnail.displayName = "CardGenerateVideoThumbnail";

export default memo(CardGenerateVideoThumbnail);